import { BreakPointCondition, UI, useBreakpointIf, HStack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { ISkeletonProps, Skeleton } from 'native-base';
import React from 'react';

import { ProductsListBar } from '../../../../../../../components/ProductsListBar';
import { ExportPdfDropdown } from '../../../../shared/ExportPdfDropdown';

import { getNoticeText } from './constants';

import { ProductDistributionHeaderTypes } from '.';

export const ColoredSkeleton = (props: ISkeletonProps) => (
  <Skeleton {...props} endColor={Color['brand.primary.lightest']} rounded={'full'} />
);

export const ProductDistributionHeaderDesktop: React.FC<ProductDistributionHeaderTypes.Props> = ({
  isLoading,
  products,
  totalValue,
  hasDateFilter = false,
  hasError,
  hasPdfExport = false,
  isExportingPdf = false,
  isToday,
  onDateSelected,
  onDownloadPdf,
  onSendToClient,
  selectedDate,
  principalTitle = 'Meus investimentos',
  principalTitleProps,
}) => {
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');
  const buttonDisabled = hasError || products.length === 0 || isExportingPdf;

  const [activeDataKey, setActiveDataKey] = React.useState<string>();

  const getTotalInvested = () => {
    if (hasError) {
      return '--';
    }
    return Utils.Mask.ScaleMoney(totalValue);
  };

  const handleClearDate = () => {
    onDateSelected?.(undefined);
  };

  const handleSelectDate = (date: Date) => {
    onDateSelected?.(date);
  };

  const noticeMemoized = React.useMemo(
    () => (
      <>
        {!!selectedDate && (
          <UI.Notice
            testId="notice-not-today"
            type="warning"
            backgroundColor={Utils.ColorToken.WithOpacity(
              'feedback.attention.lightest',
              'opacity.medium'
            )}
            textObjects={[{ text: getNoticeText(selectedDate), variant: 'regular' }]}
          />
        )}
      </>
    ),
    [selectedDate]
  );

  if (isLoading) {
    return (
      <VStack space={Spacing['stack.xxs']}>
        <HStack justifyContent="space-between" space={Spacing['inline.nano']}>
          <VStack space={Spacing['stack.nano']}>
            <ColoredSkeleton height={Typography['fontsize.xs']} width={100} />
            <ColoredSkeleton height={Typography['fontsize.xs']} width={100} />
          </VStack>
          <HStack space={Spacing['inline.xxs']}>
            <ColoredSkeleton height={Typography['fontsize.md']} width={140} />
            <ColoredSkeleton height={Typography['fontsize.md']} width={140} />
          </HStack>
        </HStack>
        <ColoredSkeleton height={Typography['fontsize.md']} width="100%" />
        <HStack space={Spacing['inline.xxxs']}>
          {Array.from({ length: 5 }).map((_, index) => (
            <ColoredSkeleton key={index} height={Typography['fontsize.md']} width={60} />
          ))}
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack space={Spacing['stack.xxs']} zIndex={5}>
      <HStack justifyContent="space-between" zIndex={6}>
        <VStack space={Spacing['stack.quarck']}>
          <UI.Text
            variant="semibold"
            fontSize={Typography['fontsize.xl']}
            textTransform="uppercase">
            {getTotalInvested()}
          </UI.Text>
          <UI.Text
            variant="semibold"
            color="neutral.medium"
            textTransform="uppercase"
            {...principalTitleProps}>
            {principalTitle}
          </UI.Text>
        </VStack>
        <HStack space={Spacing['inline.xxs']}>
          {hasDateFilter && (
            <HStack space={Spacing['inline.xxxs']} alignItems="center">
              {!isToday && !isTablet && !!selectedDate && noticeMemoized}
              <HStack alignItems="center" space={Spacing['inline.quarck']}>
                <UI.Icon name="Calendar" size="xxxs" color={Color['neutral.medium']} />
                <UI.Text color="neutral.medium">Posição por data:</UI.Text>
              </HStack>
              <HStack maxW="150px">
                <UI.DatePicker
                  onClear={handleClearDate}
                  onSelectDate={handleSelectDate}
                  calendarPosition="right"
                  placeholder="Data atual"
                  isDisabled={isExportingPdf}
                  date={selectedDate}
                />
              </HStack>
            </HStack>
          )}
          {hasPdfExport && (
            <HStack alignItems="center" space={Spacing['inline.xxxs']}>
              <ExportPdfDropdown
                isExporting={isExportingPdf}
                isDisabled={buttonDisabled}
                onDownload={() => onDownloadPdf?.()}
                onSendToClient={() => onSendToClient?.()}
              />
            </HStack>
          )}
        </HStack>
      </HStack>
      {!isToday && !!isTablet && !!selectedDate && noticeMemoized}
      <ProductsListBar
        products={products.map((p) => ({
          color: p.colorNormalized,
          dataKey: p.marketNameNormalized,
          label: p.marketNameNormalized,
          percentage: p.percentageNormalized,
          percentageFormatted: Utils.Mask.Percentage(p.percentageNormalized),
          value: p.valueNormalized,
        }))}
        setActiveDataKey={setActiveDataKey}
        activeDataKey={activeDataKey}
        tooltipDirection="bottom"
      />
    </VStack>
  );
};
